import { ViewportScroller } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import * as Util from '../../../shared/util';
import { States } from 'app/_models/constants';
import CustomValidators from '../../../shared/custom-validators';
import { PermitsService } from 'app/forests/services/permits.service';
import { WindowRef } from 'app/_services/native-window.service';
import { distinctUntilChanged } from 'rxjs/operators';
import { Title } from '@angular/platform-browser';
import * as moment from 'moment';

@Component({
    selector: 'app-purchase-permit',
    templateUrl: './purchase-permit.component.html',
    styleUrls: ['./purchase-permit.component.scss'],
    standalone: false
})
export class PurchasePermit implements OnInit {
  purchasePermitForm: FormGroup;
  productQuantities: number[] = [];
  permitExpirationDate: any = {
    expDateShort: '',
    expDateLong: ''
  };
  associatedCharges = 0;
  states = States;
  fullName: string;
  idOtherDescriptionRequired = false;
  redirecting = false;
  includeVehicleInfo = false;
  idTypes = [
    `Driver's License`,
    `Passport`,
    `Tribal ID`,
    `Immigration & Naturalization Service`,
    `State ID (Other)`,
    `Federal ID (Other)`
  ];

  @Input() forest: any;
  @Input() permit: any;
  @Output('prev') prev: EventEmitter<any> = new EventEmitter();
  @Output('next') next: EventEmitter<any> = new EventEmitter();

  constructor(
    private formBuilder: FormBuilder,
    private viewPortScroller: ViewportScroller,
    public applicationService: PermitsService,
    private winRef: WindowRef,
    private titleService: Title
  ) {
    this.titleService.setTitle("USDA Forest Service E-Permits - Purchase Permit")
  }

  navLinks = [
    { id: 'generalInformation', name: 'General Information'},
    { id: 'productQuantity', name: "Product Quantity"},
    { id: 'permitteeInformation', name: "Permittee Information"},
    { id: 'permitteeIdentifiation', name: "Permittee Identification"},
    { id: 'vehicleInformation', name: 'Vehicle Information'},
    { id: 'electronicSignature', name: 'Electronic Signature'},
  ];
  permitNotAvailable: boolean = false;

  ngOnInit() {
    this.viewPortScroller.scrollToPosition([0, 0])
    this.initForm();
    this.setProductQuantities();
    if (this.forest.product.associated_charges.length > 0) {
      this.forest.product.associated_charges.forEach(charge => { this.associatedCharges += charge.ac_per_uom })
    }
    this.permitExpirationDate.expDateShort = this.forest.product.expirationDate;
    this.permitExpirationDate.expDateLong = this.forest.product.expirationDateLong;
  }

  initForm() {
    this.purchasePermitForm = this.formBuilder.group({
      product_quantity: [null, Validators.required],
      first_name: [null, [Validators.required, Validators.maxLength(50), Validators.pattern(/^[a-zA-Z0-9\-.\, ()/|]*$/)]],
      last_name: [null, [Validators.required, Validators.maxLength(50), Validators.pattern(/^[a-zA-Z0-9\-.\, ()/|]*$/)]],
      full_name: [null],
      address_1: [null, [Validators.required, Validators.maxLength(50)]],
      address_2: [null, Validators.maxLength(50)],
      city: [null, [Validators.required, Validators.maxLength(50), Validators.pattern(/^[a-zA-Z0-9\-.\, ]*$/)]],
      state: [null, Validators.required],
      zip_code: [null, [Validators.required, Validators.maxLength(20), Validators.pattern(/^[0-9\-]*$/)]],
      email_address: [null, [Validators.required, Validators.maxLength(50), Validators.email, Validators.pattern(CustomValidators.emailPattern)]],
      confirm_email_address: [null, Validators.required],
      id_type: [null, Validators.required],
      id_state: [null],
      id_number: [null, [Validators.required, Validators.maxLength(50)]],
      phone_number: [null, [Validators.pattern(/^\d{3}-\d{3}-\d{4}$/)]],
      id_other_description: [{value: null, disabled: true}, [Validators.maxLength(100)]],
      vehicle_year: [null, [Validators.maxLength(4), Validators.pattern(/^[0-9]\d*$/)]], //, Validators.pattern(/^[0-9]*$/)
      vehicle_make: [null, Validators.maxLength(20)],
      vehicle_model: [null, Validators.maxLength(20)],
      vehicle_color: [null, [Validators.maxLength(20), Validators.pattern(/^[A-Za-z]+$/)]],
      vehicle_license_plate: [null, [Validators.maxLength(20), Validators.pattern(/^[a-zA-Z0-9]*$/)]],
      vehicle_state: [null],
      permittee_signature: [null, [Validators.required, Validators.maxLength(50)]]
    }, {
      validators: [
        CustomValidators.match('email_address', 'confirm_email_address'),
        CustomValidators.match('full_name', 'permittee_signature', true)
      ]
    })

    this.onChanges();
  }

  onChanges() {
    let firstName = '';
    let lastName = '';
    this.purchasePermitForm.controls.first_name.valueChanges.subscribe((res) => {
      firstName = res?.trim();
      this.purchasePermitForm.patchValue({full_name: `${firstName} ${lastName}`})
    });
    this.purchasePermitForm.controls.last_name.valueChanges.subscribe((res) => {
      lastName = res?.trim();
      this.purchasePermitForm.patchValue({full_name: `${firstName} ${lastName}`})
    });
    this.purchasePermitForm.controls.id_type.valueChanges.subscribe((val) => {
      if (val.includes('Other')) {
        this.purchasePermitForm.controls.id_other_description.enable();
        this.purchasePermitForm.controls.id_other_description.addValidators(Validators.required);
      } else {
        this.purchasePermitForm.controls.id_other_description.removeValidators(Validators.required);
        this.purchasePermitForm.controls.id_other_description.disable();
        this.purchasePermitForm.controls.id_other_description.setValue(null);
      }
      this.purchasePermitForm.controls.id_other_description.updateValueAndValidity();
      this.idOtherDescriptionRequired = this.purchasePermitForm.controls.id_other_description.hasValidator(Validators.required);
    });

    this.purchasePermitForm.controls.vehicle_year.valueChanges.pipe(distinctUntilChanged()).subscribe((val) => this.setVehicleRequiredOnFormsUpdate(val));
    this.purchasePermitForm.controls.vehicle_make.valueChanges.pipe(distinctUntilChanged()).subscribe((val) => this.setVehicleRequiredOnFormsUpdate(val));
    this.purchasePermitForm.controls.vehicle_model.valueChanges.pipe(distinctUntilChanged()).subscribe((val) => this.setVehicleRequiredOnFormsUpdate(val));
    this.purchasePermitForm.controls.vehicle_color.valueChanges.pipe(distinctUntilChanged()).subscribe((val) => this.setVehicleRequiredOnFormsUpdate(val));
    this.purchasePermitForm.controls.vehicle_license_plate.valueChanges.pipe(distinctUntilChanged()).subscribe((val) => this.setVehicleRequiredOnFormsUpdate(val));
    this.purchasePermitForm.controls.vehicle_state.valueChanges.pipe(distinctUntilChanged()).subscribe((val) => this.setVehicleRequiredOnFormsUpdate(val));
  }

  setVehicleRequiredOnFormsUpdate(val: any) {
    if (val && val.length && !this.includeVehicleInfo) {
      this.includeVehicleInfo = true;
      this.setVehicleFieldsRequired();
    }
  }

  toggleIncludeVehicle(include: any): void {
    if (include) {
      this.setVehicleFieldsRequired();
    } else {
      this.setVehicleFieldsNotRequired();
      this.resetVehicleFields();
    }
  }

  setVehicleFieldsRequired(): void {
    this.purchasePermitForm.controls.vehicle_year.addValidators(Validators.required);
    this.purchasePermitForm.controls.vehicle_make.addValidators(Validators.required);
    this.purchasePermitForm.controls.vehicle_model.addValidators(Validators.required);
    this.purchasePermitForm.controls.vehicle_color.addValidators(Validators.required);
    this.purchasePermitForm.controls.vehicle_license_plate.addValidators(Validators.required);
    this.purchasePermitForm.controls.vehicle_state.addValidators(Validators.required);

    this.updateVehicleValidators();
  }

  setVehicleFieldsNotRequired(): void {
    this.purchasePermitForm.controls.vehicle_year.removeValidators(Validators.required);
    this.purchasePermitForm.controls.vehicle_make.removeValidators(Validators.required);
    this.purchasePermitForm.controls.vehicle_model.removeValidators(Validators.required);
    this.purchasePermitForm.controls.vehicle_color.removeValidators(Validators.required);
    this.purchasePermitForm.controls.vehicle_license_plate.removeValidators(Validators.required);
    this.purchasePermitForm.controls.vehicle_state.removeValidators(Validators.required);

    this.updateVehicleValidators();
  }

  updateVehicleValidators(): void {
    this.purchasePermitForm.controls.vehicle_year.updateValueAndValidity();
    this.purchasePermitForm.controls.vehicle_make.updateValueAndValidity();
    this.purchasePermitForm.controls.vehicle_model.updateValueAndValidity();
    this.purchasePermitForm.controls.vehicle_color.updateValueAndValidity();
    this.purchasePermitForm.controls.vehicle_license_plate.updateValueAndValidity();
    this.purchasePermitForm.controls.vehicle_state.updateValueAndValidity();
  }

  resetVehicleFields(): void {
    this.purchasePermitForm.controls.vehicle_year.setValue(null);
    this.purchasePermitForm.controls.vehicle_make.setValue(null);
    this.purchasePermitForm.controls.vehicle_model.setValue(null);
    this.purchasePermitForm.controls.vehicle_color.setValue(null);
    this.purchasePermitForm.controls.vehicle_license_plate.setValue(null);
    this.purchasePermitForm.controls.vehicle_state.setValue(null);
  }

  setProductQuantities() {
    let min = this.forest.product.min_units;
    let max: number;
    if (this.forest.product.max_units) {
      // FUTURE PROOF CHECK TO ENSURE TOTAL DOES NOT EXCEED $300
      if (max * this.forest.product.wood_cost_per_unit > 300) {
        max = Math.floor(300 / this.forest.product.wood_cost_per_unit);
      } else {
        max = this.forest.product.max_units;
      }
    } else {
      max = Math.floor(300 / this.forest.product.wood_cost_per_unit);
    }
    if (min % 1 > 0) {
      this.productQuantities.push(min);
      min = Math.ceil(min);
    }
    for (let i = min; i <= max; i++) {
      this.productQuantities.push(i)
    }
  }

  callPrev() {
    this.prev.emit();
  }

  callNext() {
    this.next.emit();
  }

  /**
   * Create application and redirect to pay.gov.
   * If errors, return to application form.
   */
  createApplication() {
    const data = {
      forestCode: this.forest.code,
      firstName: this.purchasePermitForm.value.first_name,
      lastName: this.purchasePermitForm.value.last_name,
      emailAddress: this.purchasePermitForm.value.email_address,
      quantity: 1,
      productQuantity: this.purchasePermitForm.value.product_quantity,
      productUnitType: this.forest.product.unit_type,
      product: this.forest.product.type,
      permitExpireDate: this.permitExpirationDate.expDateShort,
      permitEffectiveDate: `${Util.mm}/${Util.dd}/${Util.yyyy}`,
      permitteeInfo: {
        address_1: this.purchasePermitForm.value.address_1,
        address_2: this.purchasePermitForm.value.address_2,
        city: this.purchasePermitForm.value.city,
        state: this.purchasePermitForm.value.state,
        zip_code: this.purchasePermitForm.value.zip_code,
        phone_number: this.purchasePermitForm.value.phone_number,
        id_info: {
          id_type: this.purchasePermitForm.value.id_type,
          id_state: this.purchasePermitForm.value.id_state,
          id_number: this.purchasePermitForm.value.id_number
        },
        vehicle_info: {
          vehicle_make: this.purchasePermitForm.value.vehicle_make,
          vehicle_model: this.purchasePermitForm.value.vehicle_model,
          vehicle_color: this.purchasePermitForm.value.vehicle_color,
          vehicle_year: this.purchasePermitForm.value.vehicle_year,
          vehicle_state: this.purchasePermitForm.value.vehicle_state,
          vehicle_license_plate: this.purchasePermitForm.value.vehicle_license_plate
        }
      }
    };

    this.redirecting = true
    this.applicationService.create(JSON.stringify(data)).subscribe((response: any) => {
      this.winRef.getNativeWindow().location.href = `${response.payGovUrl}?token=${response.token}&tcsAppID=${response.tcsAppID}`;
    }, (error: any) => {
      if (error && error[0]?.status === 404) {
        this.permitNotAvailable = true;
      }
      window.location.hash = '';
      this.winRef.getNativeWindow().scroll(0, 0);
      this.redirecting = false
    });
  }

  scrollToTop(event: Event) {
    event.preventDefault();
    this.viewPortScroller.scrollToPosition([0, 0])
  }

  scroll(event: Event, elementId: string) {
    event.preventDefault();
    this.viewPortScroller.scrollToAnchor(elementId);
  }

  toggleHover(element: HTMLElement | any) {
    element.classList.toggle('popup-hide');
  }
}
